import { QueryClient } from '@tanstack/react-query';

/**
 * Extract HTTP status from an error, if present.
 * Supports the shape produced by useTenantQuery's wrapped error
 * (which attaches `status` to the Error instance via Object.assign)
 * as well as raw Axios/fetch errors.
 */
function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof e.status === 'number') return e.status;
  if (e.response && typeof e.response.status === 'number') return e.response.status;
  return undefined;
}

/**
 * Retry decision used by every tenant-scoped query.
 *
 * Rationale:
 * - 4xx other than 408/425/429 are deterministic — retrying wastes the user's
 *   time. Surface them immediately.
 * - 429 (rate limited), 408 (request timeout), 425 (too early), and 5xx are
 *   transient. We retry up to 3 times so a brief burst from the user (or a
 *   misbehaving downstream service) does not flush cached state to "no data."
 * - Network failures (no `status`) get one retry — Axios will already have
 *   waited for its own timeout.
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = getErrorStatus(error);

  // No status: likely a network error or aborted request. Allow one retry.
  if (status === undefined) {
    return failureCount < 1;
  }

  // Transient: retry up to 3 times with exponential backoff.
  if (status === 429 || status === 408 || status === 425 || status >= 500) {
    return failureCount < 3;
  }

  // 401/403/404/422 and other deterministic 4xx: do not retry.
  return false;
}

/**
 * Exponential backoff: 1s, 2s, 4s (capped at 8s).
 * Note: React Query does not pass headers to retryDelay, so we cannot
 * honor `Retry-After` here. Components that want to respect a server-
 * provided Retry-After should refetch on their own schedule.
 */
function queryRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
    },
    mutations: {
      // Mutations are user-initiated and idempotency is not guaranteed,
      // so we leave the default (no retry) in place.
      retry: false,
    },
  },
});

// Re-exported so callers (the active-incident sync, ErrorBoundaries, etc.)
// can distinguish transient failures from real "no data" responses without
// duplicating the parsing logic.
export { getErrorStatus };
